<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import api from '@/client/core/api'
import { TrackingService } from '@/client/services'
import { useSystemStore } from '@/client/store/useSystemStore'

import NavBarProfile from './NavBarProfile.vue'

export default defineComponent({
  components: {
    NavBarProfile
  },
  computed: {
    ...mapState(useSystemStore, ['groups', 'currentGroup', 'guestGroups'])
  },
  methods: {
    ...mapActions(useSystemStore, ['setCurrentGroup', 'resetUser']),
    async selectDropdown(command: 'setting_user' | 'setting_group' | 'supports' | 'logout' | number): Promise<void> {
      if (typeof command === 'string') {
        switch (command) {
          case 'setting_user':
            return await this.clickUser()
          case 'setting_group':
            return await this.clickGroup()
          case 'supports':
            return await this.clickSupports()
          case 'logout':
            return await this.logout()
          default:
            break
        }
      }

      TrackingService.sendEvent('click:グループ名')

      if (this.currentGroup && command === this.currentGroup.id) return

      await this.setCurrentGroup(command)

      const currentGroup = this.currentGroup
      if (!currentGroup) return

      // ゲストグループの場合はアカウント設定画面に遷移
      if (this.guestGroups.some(v => v.id === currentGroup.id)) {
        await this.$router.push({ name: 'setting_group', hash: '#accounts' })
        return
      }

      if (!currentGroup.contractConfirmAt) {
        await this.$router.push({ name: 'group_billing' })
        return
      }

      if (this.$route.name !== 'campaigns') {
        await this.$router.push({ name: 'campaigns' })
      }
    },

    handleVisibleChange(): void {
      TrackingService.sendEvent('select:アカウント情報')
    },

    async logout(): Promise<void> {
      TrackingService.sendEvent('click:ログアウト')

      await api.delete('/me/session')

      await this.$router.push({ name: 'login' })

      await this.resetUser()
    },

    async clickUser(): Promise<void> {
      TrackingService.sendEvent('click:ユーザー設定')

      if (this.$route.name === 'setting_user') return

      await this.$router.push({ name: 'setting_user' })
    },

    async clickGroup(): Promise<void> {
      TrackingService.sendEvent('click:グループ設定')

      if (this.$route.name === 'setting_group') return

      await this.$router.push({ name: 'setting_group', hash: '#accounts' })
    },

    async clickSupports(): Promise<void> {
      TrackingService.sendEvent('click:サポート履歴')

      if (this.$route.name === 'supports') return

      await this.$router.push({ name: 'supports' })
    }
  }
})
</script>

<template>
  <el-dropdown trigger="click" @command="selectDropdown" @visible-change="handleVisibleChange">
    <NavBarProfile />
    <el-dropdown-menu slot="dropdown">
      <el-dropdown-item command="setting_user">
        <i class="mdi mdi-account-settings" />
        ユーザー設定
      </el-dropdown-item>
      <template
        v-if="currentGroup && currentGroup.contractConfirmAt && guestGroups.every(v => v.id !== currentGroup.id)"
      >
        <el-dropdown-item command="setting_group">
          <i class="mdi mdi-settings" />
          グループ設定
        </el-dropdown-item>
      </template>
      <el-dropdown-item command="supports">
        <i class="mdi mdi-face-agent" />
        サポート履歴
      </el-dropdown-item>
      <el-dropdown-item command="logout">
        <i class="mdi mdi-logout-variant" />
        ログアウト
      </el-dropdown-item>
      <template v-if="groups.length">
        <div class="dropdown-group-title">グループ</div>
        <div class="dropdown-group-scroll">
          <el-dropdown-item v-for="group in groups" :command="group.id" :key="group.id">
            <div class="dropdown-group-item" :class="{ 'is-current': currentGroup && group.id === currentGroup.id }">
              {{ group.name }}
              <template v-if="currentGroup && group.id === currentGroup.id">
                <i class="mdi mdi-check" />
              </template>
            </div>
          </el-dropdown-item>
        </div>
      </template>
      <template v-if="guestGroups.length">
        <div class="dropdown-group-title">ゲストグループ</div>
        <div class="dropdown-group-scroll">
          <el-dropdown-item v-for="guestGroup in guestGroups" :command="guestGroup.id" :key="guestGroup.id">
            <div
              class="dropdown-group-item"
              :class="{ 'is-current': currentGroup && guestGroup.id === currentGroup.id }"
            >
              {{ guestGroup.name }}
              <template v-if="currentGroup && guestGroup.id === currentGroup.id">
                <i class="mdi mdi-check" />
              </template>
            </div>
          </el-dropdown-item>
        </div>
      </template>
    </el-dropdown-menu>
  </el-dropdown>
</template>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.dropdown-group-title {
  padding: $spacing-unit * 2 $spacing-unit 0;
  margin: $spacing-unit * 2 0 $spacing-unit;
  color: $sub-text-color;
  border-top: 1px solid rgb(0 0 0 / 12%);
}

.dropdown-group-scroll {
  max-height: 25vh;
  overflow-y: auto;
}

.dropdown-group-item {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &.is-current {
    color: $primary-color;
  }
}
</style>
